import { Text } from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'

import type { JSX } from 'react'

export const AccessRestricted_Channel = graphql(/* GraphQL */ `
  fragment AccessRestricted_Channel on Channel @_unmask {
    avatar
    service
    name
  }
`)

/**
 * `displayName` is nullable-optional on the codegen `Channel` this still
 * receives pre-producer-flip, so it can't live in the required fragment shape
 * yet; it's added back as an optional field {@link AccessDenied} reads to name
 * the channel. Collapses into the fragment once the producer flips (CORE-6384).
 */
type AccessRestrictedChannel = FragmentOf<typeof AccessRestricted_Channel> & {
  displayName?: string | null
}

/**
 * Shown when a Team Member reaches a Community route but lacks the
 * `viewComments` action. In the MVP this primarily applies to deep-linked URLs —
 * the sidebar currently filters channels at the Publish level, so the most
 * common path here is a bookmarked or shared link to a channel the actor no
 * longer has any permission row for.
 *
 * Thin wrapper over the shared {@link AccessDenied} — it supplies the
 * Community-specific copy while inheriting the unified design (heading, icon,
 * colour, support link) shared across Publish, Community, and Insights
 * (CORE-6013).
 *
 * When a `channel` is provided (single-channel view) it is named directly and
 * shown with its avatar. Without one (All Channels view, where no channel is
 * viewable) it falls back to a generic lock icon and copy.
 */
export const AccessRestricted = ({
  channel,
}: {
  channel?: AccessRestrictedChannel
}): JSX.Element => {
  return (
    <AccessDenied
      channel={channel}
      description={
        channel ? (
          <>
            You don&apos;t have access to Community for{' '}
            <Text weight="medium">{getChannelNameForDisplay(channel)}</Text>
            <br />
            Ask an admin on your team to update your permissions.
          </>
        ) : (
          <>
            You cannot view Community for this channel.
            <br />
            To get access, ask an admin to update your permissions.
          </>
        )
      }
    />
  )
}
